import { useMutation, useQueryClient } from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { queryKeysUser } from '../queryKeys';

import {
  type UpdateUserMutationOptions,
  type UpdateUserPayload,
} from './types';
import { updateUserAdmin } from './updateUserAdmin';

/**
 * Custom hook for updating a user with a given userId as ADMIN.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Admin/operation/update_user_admin_users__id__put
 */
export const useMutationUpdateUserAdmin = (
  options?: UpdateUserMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      userData,
    }: {
      userId: UUID;
      userData: UpdateUserPayload;
    }) => updateUserAdmin(userId, userData),
    onSettled(_data, _error, { userId }) {
      queryClient.invalidateQueries({
        queryKey: queryKeysUser.getAdmin(userId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeysUser.getAllAdmin() });
    },
    ...options,
  });
};
